/**
 * Server Detail (P30 / PENDING_TODO A5.5).
 *
 * Publisher-side view of a registered MCP server:
 *   - server metadata (slug, owner, backend URL, status, risk_tier)
 *   - registered tools (from /api/v1/servers/:id/tools)
 *   - credential bindings (from /api/v1/credentials?server=:id)
 *   - associated approvals (from /api/v1/approvals?asset_id=:id)
 *
 * Differs from `/catalog/toolkits/:slug` (the consumer view) by exposing
 * lifecycle controls (promote/retire) and ops affordances (rotate creds,
 * inspect raw backend URL).
 */
import { useEffect, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Box,
  Check,
  Copy,
  Edit2,
  KeyRound,
  ListChecks,
  Lock,
  PlugZap,
  RefreshCw,
  ScrollText,
  Users,
} from 'lucide-react';

import {
  apiFetch,
  ApiError,
  useApprovals,
  useAuditEvents,
  useCredentials,
  useProbeServerTools,
  useServer,
  useServerHealth,
  useToolsForServer,
  useWrapperCredentials,
} from '@/api/index.js';
import { ActionButton } from '@/components/console/ActionButton.js';
import { useBreadcrumb } from '@/components/console/BreadcrumbContext.js';
import { DataTable, type DataRow } from '@/components/console/DataTable.js';
import { PageHeader } from '@/components/console/PageHeader.js';
import { SectionCard } from '@/components/console/SectionCard.js';
import { StatusPill } from '@/components/console/StatusPill.js';
import { AsyncShell, EmptyState } from '@/components/console/StateBlocks.js';

export default function ServerDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const server = useServer(id);
  const tools = useToolsForServer(id);
  const credentials = useCredentials(id);
  // For rest_wrapper servers, the credentials live keyed by spec slug
  // in the wrapper_credential table — not server id.
  const wrapperCredentials = useWrapperCredentials(
    server.data?.backendType === 'rest_wrapper' ? server.data.slug : undefined,
  );
  const approvals = useApprovals();
  const health = useServerHealth(id);
  const probeTools = useProbeServerTools();
  // Most-recent tool.invoke audit event — naive scan, V1 acceptable.
  const recentInvokes = useAuditEvents({ method: 'tool.invoke', limit: 50 });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { setDetailLabel } = useBreadcrumb();
  useEffect(() => {
    setDetailLabel(server.data?.name ?? null);
    return () => setDetailLabel(null);
  }, [server.data?.name, setDetailLabel]);

  // Filter approvals for this server client-side (the API doesn't expose
  // asset_id filter for read-only callers in V1).
  const myApprovals = (approvals.data?.items ?? []).filter(
    (a) => a.assetType === 'server' && a.assetId === id,
  );

  async function transition(nextStatus: 'active' | 'in_review' | 'retired'): Promise<void> {
    if (!id) return;
    setErr(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/servers/${id}`, {
        method: 'PATCH',
        body: { status: nextStatus },
      });
      await server.refetch();
    } catch (e) {
      setErr(
        e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  async function hardDelete(): Promise<void> {
    if (!id || !server.data) return;
    if (
      !window.confirm(
        `Permanently delete server "${server.data.name}" and ALL its tools? This cannot be undone.\n\nFor normal lifecycle, use Retire instead.`,
      )
    ) {
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await apiFetch(`/api/v1/servers/${id}`, { method: 'DELETE' });
    } catch (e) {
      // 404 = row already gone (concurrent delete or stale list). Treat as
      // success rather than scaring the user with a red alert.
      if (e instanceof ApiError && e.status === 404) {
        // fall through to navigate
      } else {
        setErr(
          e instanceof ApiError ? `${e.status}: ${JSON.stringify(e.body)}` : (e as Error).message,
        );
        setBusy(false);
        return;
      }
    }
    // Navigate back to publisher workspace; full reload picks up the
    // invalidated server list.
    window.location.href = '/build/publisher';
  }

  return (
    <>
      <PageHeader
        eyebrow="Publisher"
        title={server.data ? server.data.name : 'Server detail'}
        description={
          server.data
            ? `${server.data.owningWgId} · ${server.data.backendType} · risk: ${server.data.riskTier}`
            : 'Loading server…'
        }
      >
        {server.data && (
          <>
            {server.data.backendType === 'rest_wrapper' && (
              <>
                <Link to={`/build/wrappers/${server.data.slug}/edit`}>
                  <ActionButton icon={Edit2}>Edit spec</ActionButton>
                </Link>
                <Link to={`/build/wrappers/${server.data.slug}/credentials`}>
                  <ActionButton icon={Lock}>Credentials</ActionButton>
                </Link>
              </>
            )}
            <Link to={`/catalog/toolkits/${server.data.slug}`}>
              <ActionButton icon={Box}>View on catalog</ActionButton>
            </Link>
          </>
        )}
      </PageHeader>

      <AsyncShell
        isLoading={server.isLoading}
        // Treat 404 as "empty" rather than "error" — row was deleted or
        // the link is stale; either way, the user just needs an empty state
        // not a red alert.
        isError={
          server.isError && !(server.error instanceof ApiError && server.error.status === 404)
        }
        isEmpty={!server.data || (server.error instanceof ApiError && server.error.status === 404)}
        error={server.error}
        loadingTitle="Fetching server metadata"
        emptyTitle="Server not found"
        emptyHint={
          <>
            No server with id <code>{id}</code> — it may have been deleted. Go back to{' '}
            <a href="/build/publisher">Publisher Workspace</a>.
          </>
        }
      >
        {server.data && (
          <>
            {/* Live state */}
            <SectionCard
              icon={ListChecks}
              title="Live state"
              text="Backend health (auto-probed every 60s) and most recent tool invocation. Health is per-process (rest_wrapper) or per-server (native MCP)."
            >
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 14 }}>
                <div>
                  <span className="subtle">Health: </span>
                  {health.data ? (
                    <>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 9,
                          height: 9,
                          borderRadius: '50%',
                          background:
                            health.data.status === 'up'
                              ? '#2f855a'
                              : health.data.status === 'down'
                                ? '#c0392b'
                                : '#999',
                          marginRight: 6,
                          verticalAlign: 'middle',
                        }}
                      />
                      <strong>{health.data.status}</strong>
                      {health.data.httpStatus !== undefined && (
                        <span className="subtle"> · HTTP {health.data.httpStatus}</span>
                      )}
                      {health.data.latencyMs !== undefined && (
                        <span className="subtle"> · {health.data.latencyMs}ms</span>
                      )}
                      <span className="subtle">
                        {' '}
                        · checked {new Date(health.data.checkedAt).toLocaleTimeString()}
                      </span>
                      {health.data.error && (
                        <div style={{ color: '#c0392b', fontSize: 12, marginTop: 2 }}>
                          {health.data.error}
                        </div>
                      )}
                    </>
                  ) : (
                    <span className="subtle">probe pending…</span>
                  )}
                </div>
                <div>
                  <span className="subtle">Last invocation: </span>
                  {(() => {
                    const items = recentInvokes.data?.items ?? [];
                    const myToolNames = new Set(
                      (tools.data?.items ?? []).map((t) => t.name).filter(Boolean) as string[],
                    );
                    const hit = items.find((ev) => {
                      const tn = (ev.payload as { resource?: { tool_name?: string } })?.resource
                        ?.tool_name;
                      return tn && myToolNames.has(tn);
                    });
                    return hit ? (
                      <strong>{new Date(hit.created_at).toLocaleString()}</strong>
                    ) : (
                      <span className="subtle">no recent invocations</span>
                    );
                  })()}
                </div>
              </div>
            </SectionCard>

            {/* E35 — Per-domain connector URL (auto-materialised virtual_server) */}
            <ConnectorUrlPanel slug={server.data.slug} name={server.data.name} />

            {/* Lifecycle controls */}
            <SectionCard
              icon={ListChecks}
              title="Lifecycle"
              text={`Current status: ${server.data.status}. Promote to active when reviews pass; retire to remove from the catalog.`}
            >
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <StatusPill
                  label={server.data.status}
                  tone={
                    server.data.status === 'active'
                      ? 'good'
                      : server.data.status === 'in_review'
                        ? 'warn'
                        : 'neutral'
                  }
                />
                <button
                  type="button"
                  onClick={() => transition('active')}
                  disabled={busy || server.data.status === 'active'}
                  style={btnStyle(server.data.status === 'active')}
                >
                  Promote to active
                </button>
                <button
                  type="button"
                  onClick={() => transition('in_review')}
                  disabled={busy}
                  style={btnStyle(false)}
                >
                  Send for review
                </button>
                <button
                  type="button"
                  onClick={() => transition('retired')}
                  disabled={busy || server.data.status === 'retired'}
                  style={btnStyle(server.data.status === 'retired')}
                >
                  Retire
                </button>
                <button
                  type="button"
                  onClick={hardDelete}
                  disabled={busy}
                  style={{
                    ...btnStyle(false),
                    border: '1px solid #d97766',
                    color: '#c0392b',
                    marginLeft: 'auto',
                  }}
                  title="Hard-delete server + cascade tools. Use Retire for normal lifecycle."
                >
                  Delete (admin)
                </button>
              </div>
              {err && (
                <p style={{ color: '#ff8888', fontSize: 15, marginTop: 8 }} role="alert">
                  {err}
                </p>
              )}
            </SectionCard>

            {/* Backend URL */}
            <SectionCard
              icon={Box}
              title="Backend"
              text="The proxy forwards JSON-RPC envelopes to this URL when a registered tool is invoked."
            >
              <pre
                style={{
                  margin: 0,
                  padding: 12,
                  background: 'rgba(0,0,0,0.20)',
                  borderRadius: 8,
                  fontSize: 14,
                  fontFamily: 'IBM Plex Mono, monospace',
                  overflowX: 'auto',
                }}
              >
                POST {publicEndpointFor(server.data)}
                {'\n'}content-type: application/json
                {'\n'}accept: application/json, text/event-stream
                {'\n'}authorization: Bearer &lt;your-gateway-token&gt;
              </pre>
              {server.data.backendType === 'rest_wrapper' && (
                <p className="subtle" style={{ fontSize: 12, marginTop: 6 }}>
                  Internally proxies to <code>{server.data.backendUrl}</code> (loopback only — not
                  reachable from outside the gateway).
                </p>
              )}
            </SectionCard>

            {/* Tools */}
            <SectionCard
              icon={Box}
              title={`Registered tools (${tools.data?.items.length ?? 0})`}
              text={
                server.data.backendType === 'native_mcp'
                  ? 'Tools are probed from the upstream MCP server (tools/list) at register time and refreshed every 10 minutes. Click "Re-probe" to refresh now.'
                  : "Each tool inherits the server's risk tier and audit policy."
              }
            >
              {server.data.backendType === 'native_mcp' && (
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => probeTools.mutate(server.data!.id)}
                    disabled={probeTools.isPending}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: '1px solid var(--line-strong)',
                      background: 'rgba(255,255,255,0.85)',
                      fontSize: 13,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <RefreshCw size={13} />
                    {probeTools.isPending ? 'Probing…' : 'Re-probe upstream tools/list'}
                  </button>
                  {probeTools.data && (
                    <span
                      style={{
                        fontSize: 13,
                        color: probeTools.data.ok ? '#2f855a' : '#c0392b',
                      }}
                    >
                      {probeTools.data.ok
                        ? `✓ added ${probeTools.data.added}, updated ${probeTools.data.updated}, retired ${probeTools.data.retired}`
                        : `✗ ${probeTools.data.error ?? 'probe failed'}`}
                    </span>
                  )}
                </div>
              )}
              <AsyncShell
                isLoading={tools.isLoading}
                isError={tools.isError}
                isEmpty={(tools.data?.items.length ?? 0) === 0}
                error={tools.error}
                loadingTitle="Loading tools"
                emptyTitle="No tools registered yet"
                emptyHint={
                  server.data.backendType === 'native_mcp' ? (
                    <>
                      No tools detected. The upstream MCP server may have been unreachable when
                      registered. Click <strong>Re-probe upstream tools/list</strong> above.
                    </>
                  ) : (
                    <>No tools registered for this server yet.</>
                  )
                }
              >
                <DataTable
                  columns={['name', 'class', 'side_effect', 'status', 'wg access']}
                  rows={
                    (tools.data?.items ?? []).map((t) => {
                      const r = (t.restricted_to_wg_ids ?? []) as string[];
                      return {
                        name: t.name ?? '—',
                        class: t.data_classification ?? '—',
                        side_effect: t.side_effect_level ?? '—',
                        status: t.status ?? '—',
                        'wg access':
                          r.length === 0
                            ? '— open —'
                            : `${r.length} WG${r.length === 1 ? '' : 's'}`,
                      };
                    }) as readonly DataRow[]
                  }
                />
                <div style={{ marginTop: 10, textAlign: 'right' }}>
                  <Link to={`/build/servers/${server.data.id}/access`}>
                    <ActionButton icon={Users}>Manage access</ActionButton>
                  </Link>
                </div>
              </AsyncShell>
            </SectionCard>

            {/* Credentials — different shape per backend_type */}
            {server.data.backendType === 'rest_wrapper' ? (
              <SectionCard
                icon={KeyRound}
                title={`Auth realm credentials (${wrapperCredentials.data?.items.length ?? 0})`}
                text="One row per auth realm. Secrets are encrypted at rest with the gateway's secrets key and never returned over the API."
              >
                <AsyncShell
                  isLoading={wrapperCredentials.isLoading}
                  isError={wrapperCredentials.isError}
                  isEmpty={(wrapperCredentials.data?.items.length ?? 0) === 0}
                  error={wrapperCredentials.error}
                  loadingTitle="Loading credentials"
                  emptyTitle="No credentials set"
                  emptyHint={
                    <>
                      No credentials saved for this wrapper's realms.{' '}
                      <Link to={`/build/wrappers/${server.data.slug}/edit`}>Edit the spec</Link> to
                      add them.
                    </>
                  }
                >
                  <DataTable
                    columns={['realm', 'mode', 'rotated']}
                    rows={
                      (wrapperCredentials.data?.items ?? []).map((c) => ({
                        realm: c.realm_name,
                        mode: c.mode,
                        rotated: c.rotated_at ? new Date(c.rotated_at).toLocaleString() : '—',
                      })) as readonly DataRow[]
                    }
                  />
                </AsyncShell>
                <p className="subtle" style={{ fontSize: 12, marginTop: 8 }}>
                  Manage credentials inline by editing the wrapper spec, or open the{' '}
                  <Link to={`/build/wrappers/${server.data.slug}/credentials`}>
                    full credentials page
                  </Link>{' '}
                  for rotation / test-auth probes.
                </p>
              </SectionCard>
            ) : (
              <SectionCard
                icon={KeyRound}
                title={`Credential bindings (${credentials.data?.items.length ?? 0})`}
                text="P-A-trust native_mcp servers don't need a credential binding — the gateway forwards a delegated user token. This list is for external-upstream tools."
              >
                <AsyncShell
                  isLoading={credentials.isLoading}
                  isError={credentials.isError}
                  isEmpty={(credentials.data?.items.length ?? 0) === 0}
                  error={credentials.error}
                  loadingTitle="Loading credentials"
                  emptyTitle="No credentials bound"
                  emptyHint={
                    <>
                      No credentials bound. P-A-trust servers don't need one — the gateway mints a
                      delegation JWT per call.
                    </>
                  }
                >
                  <DataTable
                    columns={['mode', 'secret_ref']}
                    rows={
                      (credentials.data?.items ?? []).map((c) => ({
                        mode: c.mode,
                        secret_ref: c.secretRef,
                      })) as readonly DataRow[]
                    }
                  />
                </AsyncShell>
              </SectionCard>
            )}

            {/* Approvals */}
            <SectionCard
              icon={ScrollText}
              title={`Approval history (${myApprovals.length})`}
              text="Every status transition + tool registration also lands in the audit explorer."
            >
              {myApprovals.length === 0 ? (
                <EmptyState
                  title="No approval requests for this server"
                  hint={<>This server has not been through the approval queue yet.</>}
                  compact
                />
              ) : (
                <DataTable
                  columns={['id', 'state', 'risk', 'requester', 'sla', 'created']}
                  rows={
                    myApprovals.map((a) => ({
                      id: a.id.slice(-10),
                      state: a.state,
                      risk: a.riskTier,
                      requester: a.requesterSub,
                      sla: a.slaDueAt ?? '—',
                      created: new Date(a.createdAt).toLocaleString(),
                    })) as readonly DataRow[]
                  }
                />
              )}
            </SectionCard>
          </>
        )}
      </AsyncShell>
    </>
  );
}

/**
 * Public-facing MCP endpoint for an MCP client (Claude Desktop, etc).
 * - native_mcp: the registered backend URL is the public one (we proxy
 *   to it, but the spec is "user → gateway → backend" with the same
 *   path).
 * - rest_wrapper: the stored backend URL is the loopback wrapper service
 *   (http://127.0.0.1:4002/...) — leak that and the user can't actually
 *   reach it. The public path is gateway-fronted: `<origin>/wrappers/<slug>/mcp`.
 */
function publicEndpointFor(server: {
  backendType: string;
  backendUrl: string;
  slug: string;
}): string {
  if (server.backendType === 'rest_wrapper') {
    const origin =
      typeof window !== 'undefined' && window.location
        ? window.location.origin
        : 'https://mcp-gateway.pochub-ai.com';
    return `${origin}/wrappers/${server.slug}/mcp`;
  }
  // For native_mcp, the proxy exposes /mcp/<slug> as a virtual mount, but
  // we also accept calls to the registered backendUrl directly. Show the
  // gateway-fronted path so MCP clients have one canonical address.
  const origin =
    typeof window !== 'undefined' && window.location
      ? window.location.origin
      : 'https://mcp-gateway.pochub-ai.com';
  return `${origin}/mcp/${server.slug}`;
}

/**
 * Per-backend connector URL panel (E35).
 *
 * Every registered MCP server auto-materialises a paired virtual_server
 * served at `/v/<slug>/mcp`. That URL is what end-users paste into
 * Claude.ai's Connectors panel — one per Stanford domain so the
 * multi-MCP router can pick the right one by description.
 */
function ConnectorUrlPanel({ slug, name }: { slug: string; name: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'https://mcp-gateway.pochub-ai.com';
  const url = `${origin}/v/${slug}/mcp`;

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — user can copy manually */
    }
  }

  return (
    <SectionCard
      icon={PlugZap}
      title="Connector URL"
      text="Per-domain MCP endpoint. Auto-created when the server is registered. Share this URL with end users — they paste it into Claude.ai → Settings → Connectors → Add custom MCP connector."
    >
      <div
        style={{
          display: 'flex',
          gap: 8,
          alignItems: 'stretch',
          background: 'var(--code-bg, #f4f4f5)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          padding: 8,
          marginBottom: 8,
        }}
      >
        <code
          style={{
            flex: 1,
            fontSize: 12,
            wordBreak: 'break-all',
            alignSelf: 'center',
            fontFamily: 'monospace',
          }}
        >
          {url}
        </code>
        <button type="button" onClick={copy} style={btnStyle(false)}>
          {copied ? (
            <>
              <Check size={14} aria-hidden /> Copied
            </>
          ) : (
            <>
              <Copy size={14} aria-hidden /> Copy
            </>
          )}
        </button>
      </div>
      <p style={{ margin: 0, fontSize: 12, color: 'var(--muted)' }}>
        Suggested Claude connector name: <strong>{name}</strong>. The connector authenticates the
        end user via Stanford Google on first use; tools are scoped to their effective workgroups.
      </p>
    </SectionCard>
  );
}

function btnStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: '6px 12px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'transparent',
    color: 'inherit',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}
